// frontend/src/pages/AnalysisPage/sections/ValueSavingsAnalysis/ValueSavingsAnalysis.js
// Purpose: Provide interface for calculating and displaying value savings analysis
// Desired outcome: Allow users to select brownfield/greenfield scenario, adjust assumptions, and view calculated savings

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentFile,
  selectValueSavingsData,
  selectValueSavingsLoading,
  selectValueSavingsError,
  selectValueSavingsScenarioType,
  selectValueSavingsAssumptions,
  updateValueSavingsScenarioType,
  updateValueSavingsAssumptions,
  calculateValueSavings,
  getValueSavingsAnalysis,
  setStep
} from '../../../../state/analysis/analysisSlice';
import './ValueSavingsAnalysis.css';

const ValueSavingsAnalysis = () => {
  const currentFile = useSelector(selectCurrentFile);
  const assumptions = useSelector(selectValueSavingsAssumptions);
  const dispatch = useDispatch();
  const valueSavingsData = useSelector(selectValueSavingsData);
  const loading = useSelector(selectValueSavingsLoading);
  const error = useSelector(selectValueSavingsError);
  const scenarioType = useSelector(selectValueSavingsScenarioType);
  
  
  // Local state for form values
  const [localAssumptions, setLocalAssumptions] = useState(assumptions);
  const [localScenarioType, setLocalScenarioType] = useState(scenarioType);
  const [sortField, setSortField] = useState('totalAnnualSavings');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Load existing value savings analysis on component mount
  useEffect(() => {
    if (currentFile?.fileID) {
      dispatch(getValueSavingsAnalysis(currentFile.fileID));
    }
  }, [dispatch, currentFile]);
  
  // Update local state when Redux state changes
  useEffect(() => {
    setLocalAssumptions(assumptions);
  }, [assumptions]);
  
  useEffect(() => {
    setLocalScenarioType(scenarioType);
  }, [scenarioType]);
  
  // 🛡️ Prevent crash if assumptions are undefined (moved after hooks)
  if (!assumptions || assumptions.annualHoldingCostPct === undefined) {
    return (
      <div className="value-savings-section">
        <h2>Value Savings Analysis</h2>
        <div className="error-message">
          Required assumptions are missing. Please ensure data is loaded and try again.
        </div>
      </div>
    );
  }
  
  // Handle scenario type change
  const handleScenarioTypeChange = (e) => {
    setLocalScenarioType(e.target.value);
    dispatch(updateValueSavingsScenarioType(e.target.value));
  };
  
  // Handle assumption value change
  const handleAssumptionChange = (key, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setLocalAssumptions(prev => ({
        ...prev,
        [key]: numValue
      }));
    }
  };
  
  // Apply assumption changes to Redux state
  const applyAssumptions = () => {
    dispatch(updateValueSavingsAssumptions(localAssumptions));
  };
  
  // Reset assumptions to defaults
  const resetAssumptions = () => {
    setLocalAssumptions({
      annualHoldingCostPct: 0.20,
      obsolescencePct_current: 0.10,
      obsolescencePct_onDemand: 0.01,
      warehouseSetupCost: 100000,
      annualWarehouseOperationalCost: 50000,
      responsivenessRevenueIncreasePct: 0.02
    });
    
    dispatch(updateValueSavingsAssumptions({
      annualHoldingCostPct: 0.20,
      obsolescencePct_current: 0.10,
      obsolescencePct_onDemand: 0.01,
      warehouseSetupCost: 100000,
      annualWarehouseOperationalCost: 50000,
      responsivenessRevenueIncreasePct: 0.02
    }));
  };
  
  // Calculate value savings
  const handleCalculate = () => {
    if (currentFile?.fileID) {
      dispatch(calculateValueSavings({
        fileID: currentFile.fileID,
        scenarioType: localScenarioType,
        assumptions: localAssumptions
      }));
    }
  };
  
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };
  
  // Format percentage
  const formatPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };
  
  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Sort the results based on current sort settings
  const getSortedResults = () => {
    if (!valueSavingsData || !valueSavingsData.perSku) {
      return [];
    }
    
    return [...valueSavingsData.perSku].sort((a, b) => {
      let comparison = 0;
      
      // Handle numeric fields
      if (typeof a[sortField] === 'number' && typeof b[sortField] === 'number') {
        comparison = a[sortField] - b[sortField];
      } 
      // Handle string fields
      else if (typeof a[sortField] === 'string' && typeof b[sortField] === 'string') {
        comparison = a[sortField].localeCompare(b[sortField]);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };
  
  if (loading) {
    return (
      <div className="value-savings-section">
        <h2>Value Savings Analysis</h2>
        <div className="loading-indicator">
          <p>Processing value savings analysis...</p>
          <div className="progress-spinner"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="value-savings-section">
      <h2>Value Savings Analysis</h2>
      
      {error && (
        <div className="error-message">
          {error.message || 'An error occurred during value savings analysis.'}
        </div>
      )}
      
      <div className="analysis-settings">
        <div className="settings-card">
          <h3>Scenario Selection</h3>
          <div className="scenario-selector">
            <label>
              <input 
                type="radio"
                name="scenarioType"
                value="brownfield"
                checked={localScenarioType === 'brownfield'}
                onChange={handleScenarioTypeChange}
              />
              <div className="scenario-details">
                <span className="scenario-name">Brownfield</span>
                <span className="scenario-description">For existing infrastructure</span>
              </div>
            </label>
            
            <label>
              <input 
                type="radio"
                name="scenarioType"
                value="greenfield"
                checked={localScenarioType === 'greenfield'}
                onChange={handleScenarioTypeChange}
              />
              <div className="scenario-details">
                <span className="scenario-name">Greenfield</span>
                <span className="scenario-description">For new operations</span>
              </div>
            </label>
          </div>
        </div>
        
        <div className="settings-card">
          <h3>Assumptions</h3>
          <div className="assumptions-form">
            <div className="form-group">
              <label htmlFor="annualHoldingCostPct">Annual Holding Cost %:</label>
              <input
                id="annualHoldingCostPct"
                type="number"
                min="0"
                max="1"
                step="0.01"
              value={localAssumptions.annualHoldingCostPct ?? 0}
              onChange={(e) => handleAssumptionChange('annualHoldingCostPct', e.target.value)}
              onBlur={applyAssumptions}
              />
              <span className="input-help">{formatPercent(localAssumptions.annualHoldingCostPct)}</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="obsolescencePct_current">Current Obsolescence %:</label>
              <input
                id="obsolescencePct_current"
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={localAssumptions.obsolescencePct_current}
              onChange={(e) => handleAssumptionChange('obsolescencePct_current', e.target.value)}
              onBlur={applyAssumptions}
              />
              <span className="input-help">{formatPercent(localAssumptions.obsolescencePct_current)}</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="obsolescencePct_onDemand">On-Demand Obsolescence %:</label>
              <input
                id="obsolescencePct_onDemand"
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={localAssumptions.obsolescencePct_onDemand}
              onChange={(e) => handleAssumptionChange('obsolescencePct_onDemand', e.target.value)}
              onBlur={applyAssumptions}
              />
              <span className="input-help">{formatPercent(localAssumptions.obsolescencePct_onDemand)}</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="responsivenessRevenueIncreasePct">Responsiveness Revenue Increase %:</label>
              <input
                id="responsivenessRevenueIncreasePct"
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={localAssumptions.responsivenessRevenueIncreasePct}
              onChange={(e) => handleAssumptionChange('responsivenessRevenueIncreasePct', e.target.value)}
              onBlur={applyAssumptions}
              />
              <span className="input-help">{formatPercent(localAssumptions.responsivenessRevenueIncreasePct)}</span>
            </div>
            
            {localScenarioType === 'greenfield' && (
              <>
                <div className="form-group">
                  <label htmlFor="warehouseSetupCost">Warehouse Setup Cost:</label>
                  <input
                    id="warehouseSetupCost"
                    type="number"
                    min="0"
                    step="1000"
                    value={localAssumptions.warehouseSetupCost}
                  onChange={(e) => handleAssumptionChange('warehouseSetupCost', e.target.value)}
                  onBlur={applyAssumptions}
                  />
                  <span className="input-help">{formatCurrency(localAssumptions.warehouseSetupCost)}</span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="annualWarehouseOperationalCost">Annual Warehouse Operational Cost:</label>
                  <input
                    id="annualWarehouseOperationalCost"
                    type="number"
                    min="0"
                    step="1000"
                    value={localAssumptions.annualWarehouseOperationalCost}
                  onChange={(e) => handleAssumptionChange('annualWarehouseOperationalCost', e.target.value)}
                  onBlur={applyAssumptions}
                  />
                  <span className="input-help">{formatCurrency(localAssumptions.annualWarehouseOperationalCost)}</span>
                </div>
              </>
            )}
            
            <div className="form-actions">
              <button className="secondary-button" onClick={resetAssumptions}>Reset to Defaults</button>
              <button className="primary-button" onClick={applyAssumptions}>Apply Changes</button>
            </div>
          </div>
        </div>
        
        <div className="calculate-section">
          <button 
            className="primary-button calculate-button"
            onClick={handleCalculate}
            disabled={loading}
          >
            Calculate Value Savings
          </button>
        </div>
      </div>
      
      {valueSavingsData && (
        <div className="results-section">
          <h3>Value Savings Results</h3>
          
          <div className="summary-cards">
            <div className="summary-card">
              <h4>Total Annual Savings</h4>
              <div className="summary-value">
                {formatCurrency(valueSavingsData.summary.totalAnnualSavings)}
              </div>
            </div>
            
            <div className="summary-card">
              <h4>Inventory Holding Cost Savings</h4>
              <div className="summary-value">
                {formatCurrency(valueSavingsData.summary.totalInventoryHoldingCostSavings)}
              </div>
            </div>
            
            {localScenarioType === 'greenfield' && (
              <div className="summary-card">
                <h4>Warehouse Cost Savings</h4>
                <div className="summary-value">
                  {formatCurrency(valueSavingsData.summary.totalWarehouseSavings)}
                </div>
              </div>
            )}
            
            <div className="summary-card">
              <h4>Obsolescence Savings</h4>
              <div className="summary-value">
                {formatCurrency(valueSavingsData.summary.totalObsolescenceSavings)}
              </div>
            </div>
            
            <div className="summary-card">
              <h4>Safety Stock Savings</h4>
              <div className="summary-value">
                {formatCurrency(valueSavingsData.summary.totalSafetyStockSavings)}
              </div>
            </div>
            
            <div className="summary-card">
              <h4>Responsiveness Savings</h4>
              <div className="summary-value">
                {formatCurrency(valueSavingsData.summary.totalResponsivenessSavings)}
              </div>
            </div>
            
            <div className="summary-card">
              <h4>Price Difference Savings</h4>
              <div className="summary-value">
                {formatCurrency(valueSavingsData.summary.totalAnnualPriceDifference)}
              </div>
            </div>
            
            <div className="summary-card">
              <h4>Average Savings Per SKU</h4>
              <div className="summary-value">
                {formatCurrency(valueSavingsData.summary.averageSavingsPerSku)}
              </div>
            </div>
          </div>
          
          <h3>Per-SKU Savings Breakdown</h3>
          <div className="sku-table-container">
            <table className="sku-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('sku')} className="sortable-header">
                    SKU
                    {sortField === 'sku' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('name')} className="sortable-header">
                    Name
                    {sortField === 'name' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('category')} className="sortable-header">
                    Category
                    {sortField === 'category' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('unitProcurementPrice')} className="sortable-header">
                    Current Price
                    {sortField === 'unitProcurementPrice' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('unitPrice_onDemand')} className="sortable-header">
                    On-Demand Price
                    {sortField === 'unitPrice_onDemand' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('totalAnnualSavings')} className="sortable-header">
                    Total Savings
                    {sortField === 'totalAnnualSavings' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('leadTimePercent')} className="sortable-header">
                    Lead Time Reduction
                    {sortField === 'leadTimePercent' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {getSortedResults().map(item => (
                  <tr key={item.partID}>
                    <td>{item.sku}</td>
                    <td>{item.name}</td>
                    <td>{item.category}</td>
                    <td>{formatCurrency(item.unitProcurementPrice)}</td>
                    <td>{formatCurrency(item.unitPrice_onDemand)}</td>
                    <td>{formatCurrency(item.total_savings_usd)}</td>
                    <td>{formatPercent(item.leadTimePercent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="action-buttons">
            <button 
              className="primary-button"
              onClick={() => {
                // Recalculate to ensure latest data is in state
                handleCalculate();
                // Set step to report generation
                dispatch(setStep('reportGeneration'));
              }}
            >
              Proceed to Report Generation
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValueSavingsAnalysis;